import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import * as Sentry from '@sentry/browser';

export class Client {
    protected client: AxiosInstance;

    constructor(requestConfig: AxiosRequestConfig) {
        this.client = axios.create(requestConfig);
        /**
         * The amount of `additional` retries that are being made if original
         * request fail. We are using third party axios-retry package because
         * axios does not support retry natively.
         * @see https://www.npmjs.com/package/axios-retry
         */
        // axiosRetry(this.client, { retries: 2, retryDelay: exponentialDelay });

        /**
         * `Client interceptors` helps us debug the issues that users have
         * while using application. Every new instance of API will use those
         * interceptors. We intercept both requests and responses
         */

        /**
         * Axios timeout does not work cover every case. This code ensures that
         * Altinn request will be timed out after 30 second.
         * @see https://github.com/axios/axios/issues/647
         */
        this.client.interceptors.request.use(
            (config) => {
                const token = axios.CancelToken.source();

                setTimeout(() => {
                    token.cancel(`Timeout of ${config.timeout}ms.`);
                }, config.timeout);

                return {
                    ...config,
                    cancelToken: token.token,
                };
            },
            (error) => Promise.reject(error),
        );

        this.client.interceptors.response.use(
            (response) => this.interceptWithSentry(response),
            (error) => Promise.reject(error),
        );

        this.queueRequests();
    }

    /**
     * Prevents bursting APIs with tens or hundreds calls at the same time.
     * Some services might mark this as DDoS attack.
     * This `queueRequests` allow application to send only 5 request at the same time.
     * We do check if there is slot in a queue every `queueCheckInterval`
     * (200ms by default)`
     */
    private queueRequests = () => {
        const maximumRequestQueueSize = 5;
        const queueCheckInterval = 200;
        let requestQueue = 0;

        this.client.interceptors.request.use(function (config) {
            return new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (requestQueue < maximumRequestQueueSize) {
                        requestQueue++;
                        clearInterval(interval);
                        resolve(config);
                    }
                }, queueCheckInterval);
            });
        });

        this.client.interceptors.response.use(
            function (response) {
                requestQueue = Math.max(0, requestQueue - 1);
                return Promise.resolve(response);
            },
            function (error) {
                requestQueue = Math.max(0, requestQueue - 1);
                return Promise.reject(error);
            },
        );
    };

    /**
     * Every call that is beign made is saved into
     * `Sentry scope` and then reported to Sentry's dashboard if anything
     * goes wrong in user's browser.
     */
    private interceptWithSentry = (data: any) => {
        Sentry.withScope((scope) => {
            scope.setExtra('API Call', JSON.stringify(data, null, 2));
            Sentry.getCurrentHub().pushScope();
        });

        return data;
    };

    /**
     * This is useful when we need to send request body as FormData.
     * For example while we upload files to backend.
     */
    protected parseToFormData = (requestBody: any) => {
        const formData = new FormData();

        Object.keys(requestBody).forEach((key) => {
            if (key === 'files') {
                Array.from(requestBody['files'] as FileList).map((file) => {
                    formData.append('files', file, file.name);
                });
            } else {
                return formData.append(key, requestBody[key]);
            }
        });
        return formData;
    };

    /**
     * Intercept requests or responses before they are handled by then or catch.
     * When you use it, all requests on particular `client` will use provided
     * config.
     */
    protected interceptRequests = (interceptConfig: AxiosRequestConfig) => {
        this.client.interceptors.request.use(
            (config) => ({
                ...config,
                ...interceptConfig,
            }),
            (error) => Promise.reject(error),
        );
    };
}
